import React, { useEffect, useMemo, useState } from "react";
import { Button, Spin } from "antd";
import { ArrowLeftOutlined, CheckCircleOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { confirm_email_verification } from "../../Services/user.service";
import { VERDICTIA_WORDMARK_DARK_SRC } from "../../brand/verdictiaBrand";
import { consumeEmailVerificationTokenFromUrl } from "../../utils/emailVerificationToken";
import "./password-reset.css";

export const EMAIL_VERIFICATION_INVALID_OR_EXPIRED = "EMAIL_VERIFICATION_TOKEN_INVALID_OR_EXPIRED";

export function getEmailVerificationErrorStatus(error) {
    const data = error?.response?.data || {};
    const code = String(data?.code || data?.error || data?.detail || data?.message || "").trim().toUpperCase();

    if (!code) return "error";
    if (code === EMAIL_VERIFICATION_INVALID_OR_EXPIRED) return "invalid_or_expired";
    if (code.includes("ALREADY_USED") || code.includes("USED")) return "used";
    if (code.includes("EXPIRED")) return "expired";
    if (code.includes("INVALID")) return "invalid";
    return "error";
}

function VerifyEmail() {
    const { t } = useTranslation("login");
    const navigate = useNavigate();
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        let mounted = true;
        const rawToken = consumeEmailVerificationTokenFromUrl(window);

        if (!rawToken) {
            setStatus("missing");
            return () => {
                mounted = false;
            };
        }

        setStatus("loading");
        confirm_email_verification(rawToken)
            .then(() => {
                if (mounted) setStatus("success");
            })
            .catch((error) => {
                if (mounted) setStatus(getEmailVerificationErrorStatus(error));
            });

        return () => {
            mounted = false;
        };
    }, []);

    const statusCopy = useMemo(() => ({
        missing: t("verifyEmail.missing"),
        invalid_or_expired: t("verifyEmail.invalid_or_expired"),
        invalid: t("verifyEmail.invalid"),
        expired: t("verifyEmail.expired"),
        used: t("verifyEmail.used"),
        error: t("verifyEmail.error"),
    }), [t]);

    const goToLogin = () => navigate("/login", { replace: true });

    return (
        <main className="auth-reset-page">
            <section className="auth-reset-card" aria-labelledby="verify-email-title">
                <button className="auth-reset-back" type="button" onClick={goToLogin}>
                    <ArrowLeftOutlined />
                    <span>{t("verifyEmail.back_to_login")}</span>
                </button>

                <img className="auth-reset-logo" src={VERDICTIA_WORDMARK_DARK_SRC} alt="Verdictia" />

                <div className="auth-reset-copy">
                    <p className="auth-reset-kicker">{t("verifyEmail.kicker")}</p>
                    <h1 id="verify-email-title">{t("verifyEmail.title")}</h1>
                    <p>{t("verifyEmail.subtitle")}</p>
                </div>

                {status === "loading" && (
                    <div className="auth-reset-loading" role="status">
                        <Spin />
                        <span className="auth-reset-loading-label">{t("verifyEmail.loading")}</span>
                    </div>
                )}

                {status === "success" && (
                    <div className="auth-reset-success" role="status">
                        <CheckCircleOutlined />
                        <div>
                            <strong>{t("verifyEmail.success_title")}</strong>
                            <p>{t("verifyEmail.success_message")}</p>
                            <Button type="primary" onClick={goToLogin}>
                                {t("verifyEmail.login_cta")}
                            </Button>
                        </div>
                    </div>
                )}

                {status !== "loading" && status !== "success" && (
                    <div className="auth-reset-invalid" role="alert">
                        <SafetyCertificateOutlined />
                        <p>{statusCopy[status] || statusCopy.error}</p>
                        <Button type="default" onClick={goToLogin}>
                            {t("verifyEmail.back_to_login")}
                        </Button>
                    </div>
                )}
            </section>
        </main>
    );
}

export default VerifyEmail;
